import { useState } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDate, formatNumber, formatMoney, renderDetailValue } from "@/lib/utils";
import { defaultOutsourceUnit } from "@/lib/outsource-unit";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Pencil,
  Printer,
  Loader2,
  AlertCircle,
  Trash2,
  Truck,
  Package,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { trpcClient } from "@/lib/trpc";
import { outsourcePOsApi } from "@/lib/api-client";
import type { OutsourcePO } from "@/shared/schemas";
import { getStatusConfig } from "@/config/status-registry";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import WorkflowStepper from "@/components/shared/WorkflowStepper";
import { useWorkflowSteps } from "@/hooks/useWorkflowSteps";
import PrintDialog from "@/components/shared/PrintDialog";
import RelatedDataTabs from "@/components/shared/RelatedDataTabs";
import { OutsourceDispatchModal, OutsourceReceiptModal } from "./components";
import { CheckCircle2, PackageCheck } from "lucide-react";
import { EntityLink } from "@/components/shared/EntityLink";
import ImageLightbox from "@/components/shared/ImageLightbox";
import { DetailField as F } from "@/components/shared/DetailField";


export default function OutsourceOrderDetailPage() {
  const { orderId } = useParams({ strict: false }) as { orderId: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const [printType, setPrintType] = useState<'outsource' | 'outsource_reconciliation'>('outsource');
  const [isDispatchModalOpen, setIsDispatchModalOpen] = useState(false);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const { workflow } = useWorkflowSteps("outsourcePO");

  const handleUpdateStatus = async (newStatus: string) => {
    try {
      await trpcClient.outsourcePos.update.mutate({
        id: orderId,
        data: { status: newStatus },
      });
      await queryClient.invalidateQueries({
        queryKey: ["outsource-po", orderId],
      });
      await queryClient.invalidateQueries({ queryKey: ["activity-log"] });
      await queryClient.invalidateQueries({ queryKey: ["outsource-pos"] });
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi cập nhật trạng thái");
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await trpcClient.outsourcePos.delete.mutate({ id: orderId });
      await queryClient.invalidateQueries({ queryKey: ["outsource-pos"] });
      await queryClient.invalidateQueries({ queryKey: ["activity-log"] });

      navigate({ to: "/outsource-orders" });
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi xóa");
    } finally {
      setIsDeleting(false);
    }
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ["outsource-po", orderId],
    queryFn: async () => {
      const result = await trpcClient.outsourcePos.getById.query({
        id: orderId,
      });
      return result as OutsourcePO;
    },
  });

  const order = data as any;
  // FB-BHIADK: còn nhận được khi đơn chưa xong / chưa huỷ và chưa nhận đủ số đã gửi (hoặc SL đặt + hao hụt)
  const maxReceivable = Math.max(Number(order?.qtySentTotal || 0), Number(order?.qtyOrdered || 0) + Number(order?.allowanceQty || 0));
  const canReceive =
    !!order &&
    !["DRAFT", "COMPLETED", "CANCELLED"].includes(String(order.status)) &&
    (maxReceivable <= 0 || Number(order.qtyReceived || 0) < maxReceivable);

  // Chuỗi công đoạn gửi gia công, VD "Cán màng → Kéo lụa → Bế"; đơn cũ chỉ gắn mã công đoạn
  const stepChainText: string =
    (order?.outsourceSteps || []).map((st: any) => st.name).join(" → ") || order?.process?.name || "";
  // ĐVT số lượng gia công: theo dòng, không có thì theo công đoạn gửi đi (tờ / pcs) — FB-CYG54A
  const stepUnit = defaultOutsourceUnit(order?.outsourceSteps || []);
  // Đơn cũ lưu quy cách nối lặp theo từng dòng: chỉ hiện mỗi quy cách một lần (FB-O9IDAX)
  const uniqueSpecs: string = Array.from(
    new Set(String(order?.productSpecs || "").split(/;\s*/).map((x: string) => x.trim()).filter(Boolean)),
  ).join("; ");
  const lineItems: any[] = Array.isArray(order?.materialIssued) ? order.materialIssued : [];

  if (isLoading) {
    return (
      <StandardDetailLayout
        breadcrumbs={[{ label: "Gia công ngoài", to: "/outsource-orders" }]}
        title="Đang tải..."
      >
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto" />
        </div>
      </StandardDetailLayout>
    );
  }

  if (error || !order) {
    return (
      <StandardDetailLayout
        breadcrumbs={[{ label: "Gia công ngoài", to: "/outsource-orders" }]}
        title="Không tìm thấy"
      >
        <div className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-rose-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">
            Không tìm thấy đơn gia công
          </h3>
          <Button onClick={() => navigate({ to: "/outsource-orders" })}>
            Quay lại danh sách
          </Button>
        </div>
      </StandardDetailLayout>
    );
  }

  const percent =
    order.qtyOrdered > 0
      ? Math.round((order.qtyReceived / order.qtyOrdered) * 100)
      : 0;
  const statusCfg = getStatusConfig("OutsourcePOStatus", order.status);

  return (
    <>
      <StandardDetailLayout
        breadcrumbs={[
          { label: "Gia công ngoài", to: "/outsource-orders" },
          { label: order.poId },
        ]}
        title={order.poId}
        entityId={order?.id || orderId}
        entityType="outsource_po"
        timestamps={{ created: order.createdAt, updated: order.updatedAt }}
        status={
          <Badge variant="outline" className={statusCfg.color}>
            {statusCfg.label || order.status}
          </Badge>
        }
        actions={
          <div className="flex gap-1.5 flex-wrap">
            {/* Chỉ đơn đã lưu, chưa nghiệm thu đủ / huỷ mới xuất gửi được (máy chủ cũng chặn) */}
            {(order.status === "PENDING" || order.status === "IN_PROGRESS") && (
              <Button
                size="sm"
                onClick={() => setIsDispatchModalOpen(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5"
              >
                <Truck className="h-3.5 w-3.5" /> Xuất Gửi BTP
              </Button>
            )}
            {canReceive && (
              <Button
                size="sm"
                onClick={() => setIsReceiptModalOpen(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
              >
                <PackageCheck className="h-3.5 w-3.5" /> Nghiệm Thu Hàng
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPrintType('outsource');
                setShowPrint(true);
              }}
              className="gap-1.5"
            >
              <Printer className="h-3.5 w-3.5" /> In Đơn PO
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPrintType('outsource_reconciliation');
                setShowPrint(true);
              }}
              className="gap-1.5 text-indigo-700 border-indigo-200 hover:bg-indigo-50"
            >
              <Printer className="h-3.5 w-3.5" /> In nghiệm thu
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() =>
                navigate({
                  to: "/outsource-orders/$orderId/edit",
                  params: { orderId },
                } as any)
              }
            >
              <Pencil className="mr-1.5 h-3.5 w-3.5" /> Sửa
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-rose-600 border-rose-200 hover:bg-rose-50"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Xoá
            </Button>
          </div>
        }
        onBack={() => navigate({ to: "/outsource-orders" })}
        workflow={
          <WorkflowStepper
            workflow={workflow}
            currentStatus={order.status}
            onStatusChange={handleUpdateStatus}
          />
        }
      >
        <ConfirmDialog
          open={showDeleteConfirm}
          onOpenChange={setShowDeleteConfirm}
          title={`Xoá đơn gia công ${order.poId}?`}
          description={
            <>
              Đơn <strong>{order.poId}</strong> sẽ bị xóa vĩnh viễn.
            </>
          }
          confirmText="Xoá vĩnh viễn"
          onConfirm={handleDelete}
          isLoading={isDeleting}
          variant="destructive"
        />
        {/* ═══ 1. COMPACT 4-COL INFO GRID ═══ */}
        <div className="border rounded-lg bg-white shadow-sm p-4">
          <div className="space-y-3">
            {/* Row 1: NCC + Ref */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-2">
              <div className="col-span-1 md:col-span-2">
                <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-0.5">
                  Nhà cung cấp
                </div>
                {order.vendor ? (
                  <EntityLink
                    entityType="vendor"
                    id={order.vendor.id}
                    label={order.vendor.name}
                  />
                ) : (
                  <span className="font-medium text-slate-800">{order.vendor?.name || "—"}</span>
                )}
              </div>
              <div className="col-span-1">
                <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-0.5">
                  Mã NCC
                </div>
                {order.vendor ? (
                  <EntityLink
                    entityType="vendor"
                    id={order.vendor.id}
                    code={order.vendor.code || order.vendor.id}
                  />
                ) : (
                  <span className="font-mono text-slate-800">{order.vendor?.code || "—"}</span>
                )}
              </div>
              <div className="col-span-1">
                <F label="PO Ref" value={order.poReference} mono />
              </div>
            </div>
            <div className="border-t border-slate-100" />

            {/* Row 2: Product + Lệnh SX */}
            {(() => {
              const resolvedProductId = order.productId || order.product?.id || order.productionOrder?.productId || order.productionOrder?.product?.id || null;
              const prodImage = lineItems.length > 1 ? null : order.product?.image || order.productionOrder?.product?.image;
              const prodCode = order.product?.productCode || order.productionOrder?.product?.productCode;
              const prodName = order.product?.productName || order.product?.name || order.productionOrder?.product?.productName || order.productName;

              return (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-2 items-start">
                  <div className="col-span-1 md:col-span-2 flex items-center gap-3">
                    {prodImage ? (
                      <div className="w-11 h-11 rounded border border-slate-200 overflow-hidden shrink-0 shadow-xs">
                        <ImageLightbox
                          src={prodImage}
                          alt={prodName || "Sản phẩm"}
                          className="w-11 h-11 object-cover cursor-pointer hover:scale-105 transition-transform"
                        />
                      </div>
                    ) : (
                      <div className="w-11 h-11 rounded border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center shrink-0 text-slate-300">
                        <Package className="w-5 h-5" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-0.5">
                        Sản phẩm
                      </div>
                      {lineItems.length > 1 ? (
                        // Đơn nhiều sản phẩm: liệt kê sản phẩm của các dòng, không lấy sản phẩm của lệnh SX
                        <span className="font-medium text-slate-800" data-testid="outsource-view-multi-products">
                          {lineItems.length} sản phẩm: {lineItems.slice(0, 3).map((it: any) => it.productName).filter(Boolean).join(", ")}
                          {lineItems.length > 3 ? "…" : ""}
                        </span>
                      ) : resolvedProductId ? (
                        <EntityLink
                          entityType="product"
                          id={resolvedProductId}
                          code={prodCode}
                          label={prodName}
                        />
                      ) : (
                        <span className="font-medium text-slate-800">
                          {prodName || "—"}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="col-span-1 md:col-span-2">
                    <F label="Quy cách" value={uniqueSpecs} />
                  </div>
                  <div className="col-span-1 md:col-span-2">
                    <F
                      label="Loại gia công"
                      value={
                        order.deploymentModel === "OUTSOURCE_100" || order.deploymentModel === "outsource_100"
                          ? "Toàn phần"
                          : order.deploymentModel === "HYBRID_FINISH_EXTERNAL"
                            ? "Một phần"
                            : order.deploymentModel
                      }
                    />
                  </div>
                  <div className="col-span-1 md:col-span-2" data-testid="outsource-view-steps">
                    <F label="Công đoạn gia công" value={stepChainText || "—"} />
                  </div>
                  {order.productionOrderId && (
                    <div className="col-span-1">
                      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-0.5">
                        Lệnh SX
                      </div>
                      <EntityLink
                        entityType="productionOrder"
                        id={order.productionOrderId}
                        code={order.productionOrder?.orderId || order.productionOrderId}
                      />
                    </div>
                  )}
                  {order.orderId && (
                    <div className="col-span-1">
                      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-0.5">
                        Đơn hàng
                      </div>
                      <EntityLink
                        entityType="order"
                        id={order.orderId}
                        code={order.order?.orderId || order.orderId}
                      />
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="border-t border-slate-100" />

            {/* Row 3: Dates */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-2">
              <div className="col-span-1">
                <F label="Ngày đặt" value={formatDate(order.issueDate)} />
              </div>
              <div className="col-span-1">
                <F label="ETA giao" value={formatDate(order.expectedDate)} />
              </div>
              <div className="col-span-1">
                <F
                  label="Ngày nhận"
                  value={formatDate(order.actualReceiveDate)}
                />
              </div>
            </div>
            <div className="border-t border-slate-100" />

            {/* Row 4: Quantities + Finance */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-2">
              <div className="col-span-1">
                <F label="SL đặt" value={formatNumber(order.qtyOrdered)} />
              </div>
              <div className="col-span-1">
                <F label="SL nhận" value={formatNumber(order.qtyReceived)} />
              </div>
              <div className="col-span-1">
                <F label="Đơn giá" value={formatMoney(order.unitPrice)} mono />
              </div>
              <div className="col-span-1">
                <F
                  label="Tổng tiền"
                  value={formatMoney(order.totalAmount)}
                  hl
                />
              </div>
            </div>
            <div className="border-t border-slate-100" />

            {/* Row 5: Payment + Notes */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-2">
              <div className="col-span-1 md:col-span-2">
                <F
                  label="Thanh toán"
                  value={
                    order.paymentStatus === "PAID"
                      ? "✅ Đã thanh toán"
                      : order.paymentStatus === "PARTIAL"
                        ? "⏳ Thanh toán 1 phần"
                        : "⏹ Chưa thanh toán"
                  }
                />
              </div>
              {order.notes && (
                <div className="col-span-1 md:col-span-4 mt-2">
                  <F label="Ghi chú" value={order.notes} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ═══ 2. ITEMS TABLE ═══ */}
        {(() => {
          const items: any[] = Array.isArray(order.materialIssued)
            ? order.materialIssued
            : [];
          if (items.length === 0) {
            // Fallback: show single product if no items array
            return (
              <div className="border rounded-lg bg-white shadow-sm p-4">
                <div className="text-xs font-semibold text-slate-500 uppercase mb-2">
                  Chi tiết gia công
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2">
                  <F
                    label="Sản phẩm"
                    value={
                      order.productionOrder?.productId || order.productionOrder?.product?.id ? (
                        // PO gia công không có cột sản phẩm riêng: sản phẩm lấy theo lệnh sản xuất
                        <EntityLink
                          entityType="product"
                          id={order.productionOrder?.productId || order.productionOrder?.product?.id}
                          code={order.productionOrder?.product?.productCode}
                          label={order.productionOrder?.product?.productName || order.productName}
                        />
                      ) : (
                        order.product?.name || order.productName
                      )
                    }
                  />
                  <F label="Quy cách" value={uniqueSpecs} />
                  <F label="SL đặt" value={formatNumber(order.qtyOrdered)} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 mt-2">
                  <F
                    label="Đơn giá"
                    value={formatMoney(order.unitPrice)}
                    mono
                  />
                  <F
                    label="Tổng tiền"
                    value={formatMoney(order.totalAmount)}
                    hl
                  />
                </div>
              </div>
            );
          }
          const totalQty = items.reduce(
            (s: number, it: any) => s + (Number(it.quantity) || 0),
            0,
          );
          const totalAmt = items.reduce(
            (s: number, it: any) => s + (Number(it.amount) || 0),
            0,
          );
          return (
            <div className="border rounded-lg bg-white shadow-sm overflow-hidden">
              <div className="px-4 py-2 bg-orange-50/60 border-b flex justify-between items-center">
                <span className="text-xs font-semibold text-orange-700 uppercase">
                  Chi tiết gia công ({items.length} SP)
                </span>
                <span className="text-sm font-bold text-orange-600">
                  {formatMoney(totalAmt)}
                </span>
              </div>
              <div className="overflow-x-auto"><table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 text-[10px] text-slate-500 uppercase">
                    <th className="px-2 py-1.5 text-center w-8">STT</th>
                    <th className="px-2 py-1.5 text-left">Sản phẩm</th>
                    <th className="px-2 py-1.5 text-left">Quy cách</th>
                    <th className="px-2 py-1.5 text-left">Công đoạn GC</th>
                    <th className="px-2 py-1.5 text-right w-16">SL</th>
                    <th className="px-2 py-1.5 text-right w-24">Đơn giá</th>
                    <th className="px-2 py-1.5 text-right w-28">Thành tiền</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it: any, idx: number) => (
                    <tr key={idx} className="border-t hover:bg-slate-50/50">
                      <td className="px-2 py-1.5 text-center text-slate-400">
                        {idx + 1}
                      </td>
                      <td className="px-2 py-1.5 font-medium">
                        {it.productCode && it.productCode !== it.productName && (
                          <span className="font-mono text-[12px] font-bold text-indigo-800 bg-indigo-50 border border-indigo-200 px-1 py-px rounded mr-1">
                            {it.productCode}
                          </span>
                        )}
                        {it.productName || "—"}
                        {it.notes && (
                          <div className="text-[11px] text-amber-800 font-normal mt-0.5 whitespace-pre-wrap" data-testid="outsource-line-note">Ghi chú: {it.notes}</div>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-slate-500">
                        {it.productSpecs || "—"}
                      </td>
                      <td className="px-2 py-1.5 font-medium text-orange-700" data-testid="outsource-item-steps">
                        {stepChainText || "—"}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap" data-testid="outsource-item-qty">
                        {formatNumber(it.quantity)}{" "}
                        <span className="text-[11px] text-slate-500">{it.unit || stepUnit}</span>
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-mono">
                        {formatMoney(it.unitPrice)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-orange-600">
                        {formatMoney(it.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 bg-orange-50/40 font-semibold text-xs">
                    <td
                      colSpan={4}
                      className="px-2 py-1.5 text-right text-slate-500"
                    >
                      TỔNG
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      {formatNumber(totalQty)}
                    </td>
                    <td className="px-2 py-1.5"></td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-orange-700">
                      {formatMoney(totalAmt)}
                    </td>
                  </tr>
                </tfoot>
              </table></div>
            </div>
          );
        })()}
        {/* Tiến độ giao hàng — 1 dòng (30/10/2026): thay 3 thẻ số to, nhường chỗ cho lịch sử xuất gửi / nghiệm thu */}
        <div className="border rounded-lg bg-white shadow-sm px-4 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" data-testid="outsource-progress-line">
          <span className="flex items-center gap-1.5 font-semibold text-slate-600 uppercase">
            <Truck className="h-3.5 w-3.5 text-indigo-500" /> Tiến độ giao hàng
          </span>
          <span className="font-bold text-indigo-600 tabular-nums">{percent}%</span>
          <Progress value={percent} className="h-1.5 bg-indigo-100 w-32" />
          <span className="text-slate-500">Đã nhận <b className="text-slate-800 tabular-nums">{formatNumber(order.qtyReceived ?? 0)}</b></span>
          <span className="text-emerald-600">Đạt QC <b className="tabular-nums">{formatNumber(order.qtyAccepted ?? 0)}</b></span>
          <span className="text-rose-600">Lỗi <b className="tabular-nums">{formatNumber(order.qtyRejected ?? 0)}</b></span>
        </div>

        {/* ═══ 3. LỊCH SỬ XUẤT GỬI & NGHIỆM THU ═══ */}
        {(() => {
          const progressLogs = (order.progressLogs as any) || {};
          const dispatchHistory: any[] = progressLogs.dispatchHistory || [];
          const receiptHistory: any[] = progressLogs.receiptHistory || [];

          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Cột 1: Lịch sử Xuất gửi */}
              <div className="border rounded-lg bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-2 bg-indigo-50 border-b flex justify-between items-center">
                  <span className="text-xs font-semibold text-indigo-800 uppercase flex items-center gap-1.5">
                    <Truck className="h-3.5 w-3.5 text-indigo-600" />
                    Lịch sử xuất gửi BTP ({dispatchHistory.length} đợt)
                  </span>
                  <span className="text-xs font-bold text-indigo-600">
                    Tổng gửi: {formatNumber(order.qtySentTotal || order.qtyOrdered)}
                  </span>
                </div>
                {dispatchHistory.length === 0 ? (
                  <div className="p-4 text-center text-xs text-slate-400">
                    Chưa có đợt xuất gửi BTP nào được ghi nhận.
                  </div>
                ) : (
                  <div className="overflow-x-auto"><table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 text-[10px] text-slate-500 uppercase">
                        <th className="px-2 py-1 text-center w-8">#</th>
                        <th className="px-2 py-1 text-left">Ngày xuất</th>
                        <th className="px-2 py-1 text-right">SL gửi (pcs)</th>
                        <th className="px-2 py-1 text-left">Ghi chú</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dispatchHistory.map((d: any, idx: number) => (
                        <tr key={idx} className="border-t hover:bg-slate-50/50">
                          <td className="px-2 py-1 text-center text-slate-400">{idx + 1}</td>
                          <td className="px-2 py-1">{formatDate(d.date)}</td>
                          <td className="px-2 py-1 text-right font-semibold text-indigo-600">
                            {formatNumber(d.qty)}
                          </td>
                          <td className="px-2 py-1 text-slate-500 break-words whitespace-normal max-w-[140px]">{d.notes || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>
                )}
              </div>

              {/* Cột 2: Lịch sử Nhận & Nghiệm thu */}
              <div className="border rounded-lg bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-2 bg-emerald-50 border-b flex justify-between items-center">
                  <span className="text-xs font-semibold text-emerald-800 uppercase flex items-center gap-1.5">
                    <PackageCheck className="h-3.5 w-3.5 text-emerald-600" />
                    Lịch sử nghiệm thu nhận hàng ({receiptHistory.length} đợt)
                  </span>
                  <span className="text-xs font-bold text-emerald-600">
                    Tổng nhận: {formatNumber(order.qtyReceived || 0)}
                  </span>
                </div>
                {receiptHistory.length === 0 ? (
                  <div className="p-4 text-center text-xs text-slate-400">
                    Chưa có đợt nghiệm thu nhận hàng nào.
                  </div>
                ) : (
                  <div className="overflow-x-auto"><table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 text-[10px] text-slate-500 uppercase">
                        <th className="px-2 py-1 text-center w-8">#</th>
                        <th className="px-2 py-1 text-left">Ngày nhận</th>
                        <th className="px-2 py-1 text-right">Tổng nhận</th>
                        <th className="px-2 py-1 text-right">Đạt</th>
                        <th className="px-2 py-1 text-right">Lỗi</th>
                        <th className="px-2 py-1 text-left">Ghi chú / Lỗi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receiptHistory.map((r: any, idx: number) => (
                        <tr key={idx} className="border-t hover:bg-slate-50/50">
                          <td className="px-2 py-1 text-center text-slate-400">{idx + 1}</td>
                          <td className="px-2 py-1">{formatDate(r.date)}</td>
                          <td className="px-2 py-1 text-right font-medium">{formatNumber(r.qty || r.receivedQty)}</td>
                          <td className="px-2 py-1 text-right font-semibold text-emerald-600">
                            {formatNumber(r.acceptedQty !== undefined ? r.acceptedQty : r.qty)}
                          </td>
                          <td className="px-2 py-1 text-right font-semibold text-rose-600">
                            {formatNumber(r.rejectedQty || 0)}
                          </td>
                          <td className="px-2 py-1 text-slate-500 break-words whitespace-normal max-w-[120px]">
                            {r.defectReason ? `[Lỗi: ${r.defectReason}] ` : ""}{r.notes || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>
                )}
              </div>
            </div>
          );
        })()}
      </StandardDetailLayout>

      <OutsourceDispatchModal
        isOpen={isDispatchModalOpen}
        onClose={() => setIsDispatchModalOpen(false)}
        outsourcePO={order}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["outsource-po", orderId] });
          queryClient.invalidateQueries({ queryKey: ["outsource-pos"] });
        }}
      />

      <OutsourceReceiptModal
        isOpen={isReceiptModalOpen}
        onClose={() => setIsReceiptModalOpen(false)}
        outsourcePO={order}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["outsource-po", orderId] });
          queryClient.invalidateQueries({ queryKey: ["outsource-pos"] });
          queryClient.invalidateQueries({ queryKey: ["activity-log"] });
        }}
      />

      <PrintDialog
        open={showPrint}
        onClose={() => setShowPrint(false)}
        type={printType}
        data={order}
      />
    </>
  );
}
